import { useCallback, useEffect, useState } from 'react';
import { IconJD } from './atoms';
import {
  getDecisionLog,
  setNotifyMode,
  setNudgeTiming,
  sendNudgeFeedback,
  type DecisionLogEntry,
} from '../../api/client';

const LEAD_OPTS = [
  { label: 'Same day', value: 'same_day' as const },
  { label: '1 day before', value: '1_day' as const },
  { label: '3 days before', value: '3_days' as const },
  { label: '1 week before', value: '1_week' as const },
];

const FB_OPTS = [
  { id: 'good', apiVal: 'good' as const, label: 'Good call', learned: "Great — I'll keep timing these like this." },
  { id: 'early', apiVal: 'too_early' as const, label: 'Too early', learned: "Noted — I'll hold these a little later next time." },
  { id: 'late', apiVal: 'too_late' as const, label: 'Too late', learned: "Got it — I'll give you more runway in future." },
];

export function NudgesModal({ onClose }: { onClose: () => void }) {
  const [entries, setEntries] = useState<DecisionLogEntry[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'unavailable'>('loading');
  const [openTuneId, setOpenTuneId] = useState<string | null>(null);

  useEffect(() => {
    getDecisionLog().then((res) => {
      if (res.status === 'ok') {
        setEntries(res.entries);
        setStatus('ok');
      } else {
        setStatus('unavailable');
      }
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleMode = useCallback((entry: DecisionLogEntry) => {
    const next = entry.mode === 'nudge' ? 'quiet' : 'nudge';
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, mode: next } : e)));
    void setNotifyMode(entry.id, next);
  }, []);

  return (
    <div className="jd-modal-overlay" onClick={onClose}>
      <div className="jd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="jd-modal-head">
          <div className="jd-modal-title">Notifications</div>
          <button type="button" className="jd-icon-btn jd-modal-close" onClick={onClose}>
            <IconJD name="x" size={16} color="currentColor" stroke={2} />
          </button>
        </div>

        <div className="jd-modal-body">
          {status === 'loading' ? (
            <div className="jd-modal-empty">
              <div className="jd-spinner" />
            </div>
          ) : status === 'unavailable' ? (
            <div className="jd-modal-empty">
              <div className="jd-modal-empty-title">Notifications aren't live yet</div>
              <div className="jd-modal-empty-body">
                When the decision engine ships, this is where you'll tune what's worth a
                buzz — silent by default, one wrong nudge muted forever.
              </div>
            </div>
          ) : entries.length === 0 ? (
            <div className="jd-modal-empty">
              <div className="jd-modal-empty-title">Nothing scheduled</div>
              <div className="jd-modal-empty-body">
                Jotly hasn't decided anything's worth interrupting you for. It stays that
                way until something time-bound lands.
              </div>
            </div>
          ) : (
            <>
              <p className="jd-nudge-preamble">
                What's worth interrupting you for? Flip anything to{' '}
                <strong>Quiet</strong> and it just waits on Now — never a buzz.
                I show you exactly what I'd send, and when, so you can trust it
                before it ever fires.
              </p>
              {entries.map((entry) => (
                <NudgeCard
                  key={entry.id}
                  entry={entry}
                  onToggle={() => toggleMode(entry)}
                  tuneOpen={openTuneId === entry.id}
                  onTuneToggle={() => setOpenTuneId(openTuneId === entry.id ? null : entry.id)}
                />
              ))}
              <p className="jd-nudge-footnote">
                One wrong buzz and you'd mute me forever — so I default to quiet and only
                ask to interrupt when you'd thank me for it.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NudgeCard({
  entry, onToggle, tuneOpen, onTuneToggle,
}: {
  entry: DecisionLogEntry;
  onToggle: () => void;
  tuneOpen: boolean;
  onTuneToggle: () => void;
}) {
  const nudging = entry.mode === 'nudge';
  const isHold = entry.verdict === 'hold';

  return (
    <div className={`jd-nudge-card ${!nudging ? 'dimmed' : ''}`}>
      <div className="jd-nudge-top">
        <span className="jd-nudge-title">{entry.title}</span>
        {!isHold && (
          <button type="button" className="jd-nudge-ctl" onClick={onToggle}>
            <span className={`jd-nudge-label ${nudging ? 'on' : ''}`}>
              {nudging ? 'Nudge' : 'Quiet'}
            </span>
            <span className={`jd-nudge-switch ${nudging ? 'on' : ''}`}>
              <span className="jd-nudge-knob" />
            </span>
          </button>
        )}
      </div>

      {nudging ? (
        <>
          <div className="jd-nudge-chain">
            {entry.chain.map((step, i) => (
              <div key={`${step.label}-${i}`} className="jd-nudge-chain-row">
                <span className="jd-nudge-chain-key">{step.label}</span>
                <span className="jd-nudge-chain-val">{step.value}</span>
              </div>
            ))}
          </div>
          <div className="jd-nudge-fire">
            <div className="jd-nudge-fire-head">One buzz, at the right moment — then silence:</div>
            <div className="jd-nudge-fire-box">
              <IconJD name="bell" size={14} color="var(--ink-3)" stroke={1.6} />
              <span>{entry.message}</span>
            </div>
            <button type="button" className="jd-nudge-tune-link" onClick={onTuneToggle}>
              <IconJD name="sliders" size={12} color="var(--accent)" stroke={1.6} />
              <span>{tuneOpen ? 'Done tuning' : 'Tune timing & feedback'}</span>
              <IconJD
                name="chev"
                size={10}
                color="var(--accent)"
                stroke={1.8}
              />
            </button>
            {tuneOpen && <TunePanel entryId={entry.id} />}
          </div>
        </>
      ) : isHold ? (
        <div className="jd-nudge-quiet-row">
          <IconJD name="shield" size={13} color="var(--ink-3)" stroke={1.6} />
          <span>{entry.why ?? "No real cost to missing it — I'd never interrupt you for this."}</span>
        </div>
      ) : (
        <div className="jd-nudge-quiet-row">
          <IconJD name="moon" size={13} color="var(--ink-3)" stroke={1.6} />
          <span>Stays on Now only. I won't interrupt you — it'll be here when you look.</span>
        </div>
      )}
    </div>
  );
}

function TunePanel({ entryId }: { entryId: string }) {
  const [lead, setLead] = useState('1_day');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [never, setNever] = useState(false);

  const pickLead = (val: typeof LEAD_OPTS[number]['value']) => {
    setLead(val);
    void setNudgeTiming(entryId, val);
  };

  const pickFeedback = (id: string) => {
    if (feedback === id) { setFeedback(null); return; }
    setFeedback(id);
    setNever(false);
    const opt = FB_OPTS.find((o) => o.id === id);
    if (opt) void sendNudgeFeedback(entryId, opt.apiVal);
  };

  const toggleNever = () => {
    const next = !never;
    setNever(next);
    setFeedback(null);
    if (next) void sendNudgeFeedback(entryId, 'never');
  };

  const activeFb = FB_OPTS.find((o) => o.id === feedback);
  const learnedText = never
    ? "Done — I won't nudge you for things like this again."
    : activeFb?.learned ?? null;

  return (
    <div className="jd-nudge-tune">
      <div className="jd-nudge-tune-head">Nudge me</div>
      <div className="jd-nudge-chip-row">
        {LEAD_OPTS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`jd-nudge-chip ${lead === opt.value ? 'on' : ''}`}
            onClick={() => pickLead(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="jd-nudge-tune-head">How'd I do? &middot; I learn from this</div>
      <div className="jd-nudge-chip-row">
        {FB_OPTS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`jd-nudge-chip ${feedback === opt.id ? 'on' : ''}`}
            onClick={() => pickFeedback(opt.id)}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          className={`jd-nudge-chip danger ${never ? 'on' : ''}`}
          onClick={toggleNever}
        >
          Never nudge me for this
        </button>
      </div>

      {learnedText && (
        <div className="jd-nudge-learned">
          <IconJD name="sparkle" size={12} color="var(--accent)" stroke={1.6} />
          <span>{learnedText}</span>
        </div>
      )}
    </div>
  );
}
